import React from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ShieldMoonIcon from '@mui/icons-material/ShieldMoon';
import InlineAlert from '../../../shared/components/InlineAlert';
import StatusChip from '../../../shared/components/StatusChip';
import { domainLabel } from '../../../shared/labels';
import type { Customer } from '../../../types';
import type { CustomerReconcilerHealth, ConvergenceHealth } from '../../../types/backend';

function convergenceParts(c: ConvergenceHealth, overall: CustomerReconcilerHealth['overall']): string {
  const parts: string[] = [];
  if (c.converged) parts.push(`${c.converged} synced`);
  if (c.drifted) parts.push(`${c.drifted} drifted`);
  if (c.failed) parts.push(`${c.failed} failed`);
  if (c.in_progress) parts.push(`${c.in_progress} running`);
  if (c.pending) parts.push(`${c.pending} pending`);
  if (c.unknown) parts.push(`${c.unknown} unknown`);
  if (parts.length > 0) return parts.join(' · ');
  // No attributed counts: UNKNOWN means we couldn't determine state, not that
  // there are genuinely zero resources.
  return overall === 'UNKNOWN' ? 'convergence unknown' : 'no resources';
}

interface CustomerReconcilerTileProps {
  health: CustomerReconcilerHealth;
  selected: boolean;
  onSelect: (domain: string) => void;
}

const CustomerReconcilerTile: React.FC<CustomerReconcilerTileProps> = ({ health, selected, onSelect }) => {
  const blocked = health.blocked_by_platform === true;
  const isDown = health.overall === 'DOWN';
  const isUnknown = health.overall === 'UNKNOWN';

  return (
    <Paper
      variant="outlined"
      onClick={() => onSelect(health.domain)}
      sx={{
        p: 2,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        borderColor: blocked
          ? 'warning.main'
          : isDown
            ? 'error.main'
            : isUnknown
              ? 'text.secondary'
              : selected
                ? 'primary.main'
                : 'divider',
        borderWidth: blocked || isDown || selected ? 2 : 1,
        borderStyle: blocked || isUnknown ? 'dashed' : 'solid',
        bgcolor: blocked
          ? (t) => alpha(t.palette.warning.main, 0.08)
          : isDown
            ? (t) => alpha(t.palette.error.main, 0.08)
            : 'background.paper',
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover': {
          borderColor: blocked ? 'warning.dark' : isDown ? 'error.dark' : 'primary.main',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {domainLabel(health.domain)}
        </Typography>
        <StatusChip status={health.overall} size="small" />
      </Box>

      {blocked ? (
        <Tooltip title="The shared reconciler machine for this domain is unavailable. This is a platform-side outage, not a failure of this customer's resources.">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ShieldMoonIcon sx={{ fontSize: 16, color: 'warning.main' }} />
            <Typography variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>
              Blocked by platform (machine {health.machine_overall})
            </Typography>
          </Box>
        </Tooltip>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {convergenceParts(health.convergence, health.overall)}
        </Typography>
      )}
    </Paper>
  );
};

export interface CustomerLensPanelProps {
  selectedCustomer: Customer | null;
  customers: Customer[];
  reconcilers: CustomerReconcilerHealth[];
  skippedDomains: string[];
  loading: boolean;
  error: string | null;
  selectedDomain: string | null;
  onSelectCustomer: (customerId: string) => void;
  onSelectReconciler: (domain: string) => void;
}

const CustomerLensPanel: React.FC<CustomerLensPanelProps> = ({
  selectedCustomer,
  customers,
  reconcilers,
  skippedDomains,
  loading,
  error,
  selectedDomain,
  onSelectCustomer,
  onSelectReconciler,
}) => {
  const options = selectedCustomer && !customers.some((c) => c.id === selectedCustomer.id)
    ? [selectedCustomer, ...customers]
    : customers;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>
      <FormControl size="small" sx={{ minWidth: 280, maxWidth: 420 }}>
        <InputLabel id="customer-lens-select-label">Customer</InputLabel>
        <Select
          labelId="customer-lens-select-label"
          label="Customer"
          value={selectedCustomer?.id ?? ''}
          onChange={(e) => onSelectCustomer(e.target.value)}
        >
          {options.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {!selectedCustomer ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Select a customer to view their control-plane health.
        </Typography>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : error ? (
        <InlineAlert severity="error">{error}</InlineAlert>
      ) : (
        <>
          {reconcilers.some((r) => r.blocked_by_platform) && (
            <InlineAlert severity="warning">
              Some reconcilers are blocked by a platform-side machine outage — these are not failures of
              this customer's resources.
            </InlineAlert>
          )}

          {reconcilers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No per-customer reconciler health reporting for this customer.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                gap: 2,
              }}
            >
              {reconcilers.map((health) => (
                <CustomerReconcilerTile
                  key={health.domain}
                  health={health}
                  selected={selectedDomain === health.domain}
                  onSelect={onSelectReconciler}
                />
              ))}
            </Box>
          )}

          {skippedDomains.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Not attributable per-customer: {skippedDomains.map(domainLabel).join(', ')}.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default CustomerLensPanel;
